import { Fragment, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Settings2, Users } from 'lucide-react';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell, Button } from '@ncoder/ui';
import { UsageStatusBadge } from '@/components/atoms/org-admin/UsageStatusBadge';
import { WindowCycleToggle, type UsageScope } from '@/components/molecules/org-admin/WindowCycleToggle';
import type { OrgUsageReportMember, OrgServiceUsageReportUser } from '@/services/org-admin';
import { formatCurrencyUsd, isOverBudget, labelForService } from './usageFormat';

// Flip to true once budget editing is fully wired up end-to-end.
const SHOW_EDIT_BUDGET = false;

const SKELETON_WIDTHS = ['w-40', 'w-32', 'w-48', 'w-36'] as const;

function SkeletonMemberUsageRow({ nameWidth }: { nameWidth: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,160px)_1fr_64px] items-center gap-3 px-1.5 py-1">
      <div className={`h-3 rounded-md bg-muted-foreground/10 loading-parallax ${nameWidth}`} />
      <div className="h-3 rounded-full bg-muted-foreground/[0.07] loading-parallax" />
      <div className="ml-auto h-3 w-10 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
    </div>
  );
}

export interface MemberUsageChartProps {
  data: OrgUsageReportMember[];
  periodLabel: string;
  isLoading?: boolean;
  onEditBudget: (member: OrgUsageReportMember) => void;
  serviceByUsers: OrgServiceUsageReportUser[];
  isServiceByUsersLoading?: boolean;
}

const COLUMN_COUNT = SHOW_EDIT_BUDGET ? 6 : 5; // chevron + member + tokens + spend + spend/budget [+ edit]

export function MemberUsageChart({
  data,
  periodLabel,
  isLoading,
  onEditBudget,
  serviceByUsers,
  isServiceByUsersLoading,
}: MemberUsageChartProps) {
  const tokensOf = (row: OrgUsageReportMember) => row.prompt_tokens + row.completion_tokens;
  const cycleTokensOf = (row: OrgUsageReportMember) => row.prompt_tokens_cycle + row.completion_tokens_cycle;
  const sorted = [...data].sort((a, b) => tokensOf(b) - tokensOf(a));

  const [scope, setScope] = useState<UsageScope>('window');
  const isCycle = scope === 'cycle';

  const serviceByUserId = useMemo(() => {
    const map = new Map<string, OrgServiceUsageReportUser>();
    serviceByUsers.forEach((user) => map.set(user.user_id, user));
    return map;
  }, [serviceByUsers]);

  const [expandedUserIds, setExpandedUserIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (userId: string) => {
    setExpandedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  return (
    <div className="studio-card p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Usage by member</h3>
          <p className="text-xs text-muted-foreground">
            Token consumption and spend — {isCycle ? 'since this cycle started' : periodLabel}. Expand a row for its
            per-service breakdown.
          </p>
        </div>
        <WindowCycleToggle value={scope} onChange={setScope} />
      </div>

      <div className="mt-4 h-64 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-2">
            {SKELETON_WIDTHS.map((nameWidth, index) => (
              <SkeletonMemberUsageRow key={index} nameWidth={nameWidth} />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-sm text-muted-foreground">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <p>No members to show usage for.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Member</TableHead>
                <TableHead className="text-right">Tokens</TableHead>
                <TableHead className="text-right">{isCycle ? 'Spend (this cycle)' : `Spend (${periodLabel})`}</TableHead>
                <TableHead className="text-right">Spend / budget</TableHead>
                {SHOW_EDIT_BUDGET && <TableHead className="w-8" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((row) => {
                const serviceRows = serviceByUserId.get(row.user_id)?.by_service ?? [];
                const isExpanded = expandedUserIds.has(row.user_id);
                const hasNoServiceData = !isServiceByUsersLoading && serviceRows.length === 0;

                return (
                  <Fragment key={row.user_id}>
                    <TableRow>
                      <TableCell className="w-8">
                        {!hasNoServiceData && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            title={isExpanded ? 'Hide service breakdown' : 'Show service breakdown'}
                            onClick={() => toggleExpanded(row.user_id)}
                          >
                            {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-1.5">
                          {row.email ?? row.user_id}
                          <UsageStatusBadge blocked={row.blocked} overBudget={isOverBudget(row.spend_usd_cycle, row.budget_usd)} />
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {(isCycle ? cycleTokensOf(row) : tokensOf(row)).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrencyUsd(isCycle ? row.spend_usd_cycle : row.spend_usd_window)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatCurrencyUsd(row.spend_usd_cycle)} / {formatCurrencyUsd(row.budget_usd)}
                      </TableCell>
                      {SHOW_EDIT_BUDGET && (
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-6 w-6" title="Edit budget" onClick={() => onEditBudget(row)}>
                            <Settings2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                    {isExpanded && (
                      <TableRow key={`${row.user_id}-services`}>
                        <TableCell colSpan={COLUMN_COUNT} className="bg-muted/30 p-0">
                          <div className="p-3">
                            {isServiceByUsersLoading ? (
                              <p className="text-xs text-muted-foreground">Loading service breakdown…</p>
                            ) : serviceRows.length === 0 ? (
                              <p className="text-xs text-muted-foreground">No service activity in this window.</p>
                            ) : (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Service</TableHead>
                                    <TableHead className="text-right">{isCycle ? 'Runs (cycle)' : 'Runs'}</TableHead>
                                    <TableHead className="text-right">{isCycle ? 'Failed (cycle)' : 'Failed'}</TableHead>
                                    <TableHead className="text-right">{isCycle ? 'Input tokens (cycle)' : 'Input tokens'}</TableHead>
                                    <TableHead className="text-right">{isCycle ? 'Output tokens (cycle)' : 'Output tokens'}</TableHead>
                                    <TableHead className="text-right">{isCycle ? 'Cost (cycle)' : 'Cost'}</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {serviceRows.map((service) => (
                                    <TableRow key={service.service}>
                                      <TableCell className="font-medium">{labelForService(service.service)}</TableCell>
                                      <TableCell className="text-right tabular-nums">
                                        {isCycle
                                          ? (service.run_count_cycle?.toLocaleString() ?? '—')
                                          : service.run_count.toLocaleString()}
                                      </TableCell>
                                      <TableCell
                                        className={`text-right tabular-nums ${(isCycle ? service.failed_count_cycle : service.failed_count) ? 'text-warning' : ''}`}
                                      >
                                        {isCycle
                                          ? (service.failed_count_cycle?.toLocaleString() ?? '—')
                                          : service.failed_count.toLocaleString()}
                                      </TableCell>
                                      <TableCell className="text-right tabular-nums">
                                        {isCycle
                                          ? (service.input_tokens_cycle?.toLocaleString() ?? '—')
                                          : service.input_tokens.toLocaleString()}
                                      </TableCell>
                                      <TableCell className="text-right tabular-nums">
                                        {isCycle
                                          ? (service.output_tokens_cycle?.toLocaleString() ?? '—')
                                          : service.output_tokens.toLocaleString()}
                                      </TableCell>
                                      <TableCell className="text-right tabular-nums">
                                        {isCycle
                                          ? (service.cost_usd_cycle !== undefined ? formatCurrencyUsd(service.cost_usd_cycle) : '—')
                                          : formatCurrencyUsd(service.cost_usd)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
